import React, { useState, useEffect } from 'react';
import { API } from '../constants';
import { useTheme } from '../ThemeContext';

const LiveCounter = () => {
  const goalAmount = 88750;
  const goalDonors = 1000;
  const [amount, setAmount] = useState(0);
  const [donors, setDonors] = useState(0);
  const ref = React.useRef(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const { isInstitutional } = useTheme();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || loaded) return;
    fetch(`${API}/donations/stats`)
      .then(r => r.json())
      .then(data => {
        const targetAmount = data.total_amount || 0;
        const targetDonors = data.total_donors || 0;
        const duration = 2000;
        const start = performance.now();
        const animate = (now) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setAmount(Math.floor(eased * targetAmount));
          setDonors(Math.floor(eased * targetDonors));
          if (progress < 1) requestAnimationFrame(animate);
          else { setAmount(targetAmount); setDonors(targetDonors); setLoaded(true); }
        };
        requestAnimationFrame(animate);
      })
      .catch(() => { setAmount(0); setDonors(0); });
  }, [visible, loaded]);

  const pctAmount = ((amount / goalAmount) * 100).toFixed(1);
  const pctDonors = Math.min((donors / goalDonors) * 100, 100);

  const accent = isInstitutional ? '#C4532D' : '#00FF41';
  const accentLight = isInstitutional ? '#D4714F' : '#33FF66';
  const boxBg = isInstitutional ? '#F4F1EC' : 'rgba(10,10,10,0.8)';
  const boxBorder = isInstitutional ? '#E2DED6' : 'rgba(0,255,65,0.2)';
  const boxShadow = isInstitutional ? '0 2px 12px rgba(27,42,74,0.06)' : '0 0 30px rgba(0,255,65,0.08)';
  const bigTextColor = isInstitutional ? '#1B2A4A' : '#fff';
  const bigTextShadow = isInstitutional ? 'none' : '0 0 20px rgba(255,255,255,0.15)';
  const barTrackBg = isInstitutional ? '#E2DED6' : 'rgba(39,39,42,0.8)';
  const barGradient = isInstitutional
    ? `linear-gradient(90deg, ${accent} 0%, ${accentLight} 100%)`
    : 'linear-gradient(90deg, #00FF41 0%, #33FF66 60%, #66FF99 100%)';
  const barShadow = isInstitutional
    ? `0 0 8px rgba(196,83,45,0.3)`
    : '0 0 16px rgba(0,255,65,0.6), inset 0 1px 0 rgba(255,255,255,0.2)';

  return (
    <div ref={ref} data-testid="live-counter" className="w-full max-w-lg mx-auto mb-10">
      <div className="backdrop-blur-sm p-5 mb-4" style={{ background: boxBg, border: `1px solid ${boxBorder}`, boxShadow, borderRadius: isInstitutional ? '12px' : '0' }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs uppercase tracking-[0.2em]" style={{ color: accent, fontFamily: isInstitutional ? "'Titillium Web', sans-serif" : 'monospace', fontWeight: 700 }}>Fondatori</span>
          <span className="text-xs" style={{ color: isInstitutional ? '#8A96A5' : '#71717a', fontFamily: isInstitutional ? "'Titillium Web', sans-serif" : 'monospace' }}>{donors > 0 ? 'e tu?' : 'sii il primo'}</span>
        </div>
        <div className="flex items-baseline justify-center gap-2 mb-3">
          <span data-testid="founders-count" className="font-black text-4xl md:text-5xl" style={{ color: bigTextColor, textShadow: bigTextShadow, fontFamily: isInstitutional ? "'Source Serif 4', serif" : 'monospace' }}>
            {donors}
          </span>
          <span className="text-lg" style={{ color: isInstitutional ? '#8A96A5' : '#52525b', fontFamily: isInstitutional ? "'Titillium Web', sans-serif" : 'monospace' }}>/</span>
          <span className="text-lg" style={{ color: isInstitutional ? '#5A6B80' : '#71717a', fontFamily: isInstitutional ? "'Titillium Web', sans-serif" : 'monospace' }}>{goalDonors.toLocaleString('it-IT')}</span>
        </div>
        <div className="w-full h-3 rounded-full overflow-hidden mb-2" style={{ background: barTrackBg, border: isInstitutional ? '1px solid #EDEBE6' : '1px solid rgba(63,63,70,0.5)' }}>
          <div
            className="h-full rounded-full transition-all duration-1000 relative"
            style={{ width: `${Math.max(pctDonors, 1)}%`, background: barGradient, boxShadow: barShadow }}
          >
            <div className="absolute right-0 top-0 bottom-0 w-2 bg-white/40 rounded-full animate-pulse" />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs" style={{ color: isInstitutional ? '#8A96A5' : '#71717a', fontFamily: isInstitutional ? "'Titillium Web', sans-serif" : 'monospace' }}>{goalDonors - donors > 0 ? `mancano ${(goalDonors - donors).toLocaleString('it-IT')}` : 'obiettivo raggiunto!'}</p>
          <p className="text-xs font-bold" style={{ color: accent, fontFamily: isInstitutional ? "'Titillium Web', sans-serif" : 'monospace' }}>{pctDonors.toFixed(1)}%</p>
        </div>
      </div>

      <div className="flex items-baseline justify-center gap-2 mb-2">
        <span className="font-black text-2xl md:text-3xl" style={{ color: accent, textShadow: isInstitutional ? 'none' : '0 0 20px rgba(0,255,65,0.4)', fontFamily: isInstitutional ? "'Source Serif 4', serif" : 'monospace' }}>
          &euro;{amount.toLocaleString('it-IT')}
        </span>
        <span className="text-sm" style={{ color: isInstitutional ? '#8A96A5' : '#71717a', fontFamily: isInstitutional ? "'Titillium Web', sans-serif" : 'monospace' }}>/ &euro;{goalAmount.toLocaleString('it-IT')}</span>
      </div>
      <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: barTrackBg, border: isInstitutional ? '1px solid #EDEBE6' : '1px solid rgba(63,63,70,1)' }}>
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pctAmount}%`, background: barGradient, boxShadow: barShadow }}
        />
      </div>
      <p className="text-center text-xs mt-2 tracking-wider" style={{ color: isInstitutional ? '#8A96A5' : '#71717a', fontFamily: isInstitutional ? "'Titillium Web', sans-serif" : 'monospace' }}>{pctAmount}% RACCOLTO — OBIETTIVO CROWDFUNDING</p>
    </div>
  );
};

export default LiveCounter;
